import BaseChipSearchMultiSelect from '@components/multi-select/BaseChipSearchMultiSelect';
import { Stack, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { OFF_MESSAGE } from '@pages/cameras/details/components/EvPermitExemptionSummary';
import { EvPermitZoneOption } from '@pages/cameras/utils/EvPermitHelpers';
import color from '@theme/Colors';
import moment from 'moment';
import { useMemo } from 'react';

type Props = {
    enabled: boolean;
    /** Camera's own zone: always sent by BE, shown locked and not removable. */
    primaryZone?: EvPermitZoneOption;
    /** Extra zones picked by the user (primary zone excluded). */
    extraZones: EvPermitZoneOption[];
    onChangeExtraZones(zones: EvPermitZoneOption[]): void;
    permitLengthSeconds: number | null;
    onChangePermitLength(seconds: number | null): void;
    zoneOptions: EvPermitZoneOption[];
};

/** Body of the "EV permit exemption" form section (the on/off switch lives in the section header). */
export default function EvPermitExemptionSection(props: Props) {
    const { enabled, primaryZone, extraZones, onChangeExtraZones, permitLengthSeconds, onChangePermitLength } = props;

    // Hooks must stay above the early return below.
    const selectedZones = useMemo(
        () => (primaryZone ? [primaryZone, ...extraZones.filter((z) => z.Id !== primaryZone.Id)] : extraZones),
        [primaryZone, extraZones]
    );

    // Sorted by location so MUI groupBy renders one header per location.
    const options = useMemo(
        () => [...props.zoneOptions].sort((a, b) => a.LocationName.localeCompare(b.LocationName)),
        [props.zoneOptions]
    );

    if (!enabled) {
        return (
            <Stack border={`1px solid ${color.grey300}`} borderRadius={1} p={2}>
                <Typography variant="body1">{OFF_MESSAGE}</Typography>
            </Stack>
        );
    }

    const isPrimary = (zone: EvPermitZoneOption) => zone.Id === primaryZone?.Id;
    const permitLengthValue =
        permitLengthSeconds != null ? moment().startOf('day').add(permitLengthSeconds, 'seconds') : null;

    return (
        <Stack spacing={2}>
            <BaseChipSearchMultiSelect
                value={selectedZones}
                options={options}
                onChange={(zones) => onChangeExtraZones(zones.filter((zone) => !isPrimary(zone)))}
                getOptionKey={(zone) => zone.Id}
                getOptionLabel={(zone) => zone.PublicName}
                groupBy={(zone) => zone.LocationName}
                getSearchText={(zone) => `${zone.PublicName} ${zone.LocationName}`}
                isOptionLocked={isPrimary}
                label={
                    <span>
                        Zone(s)<span style={{ color: 'red' }}>*</span>
                    </span>
                }
                placeholder="Select zones"
                searchPlaceholder="Search location or zone"
                noOptionsText="No zones found"
            />

            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body1">
                    Permit length<span style={{ color: 'red' }}>*</span>:
                </Typography>
                <TimePicker
                    ampm={false}
                    value={permitLengthValue}
                    onChange={(value) => {
                        if (!value || !value.isValid()) return;
                        const seconds = value.hours() * 3600 + value.minutes() * 60;
                        onChangePermitLength(seconds > 0 ? seconds : null);
                    }}
                    sx={{
                        width: 150,
                        '& .MuiSvgIcon-root': {
                            width: 18,
                            height: 18,
                        },
                    }}
                />
            </Stack>
        </Stack>
    );
}
